/**
 * Apply plan diffs from conversational workflow editing.
 *
 * Takes modifications from a `modify_workflow` intent and applies them
 * to the current workflow plan and step parameters.
 *
 * @module ui/dialog/WorkflowPane/applyModifications
 */

import { getAction } from '../../../gibbs/registry/index';

/**
 * Build default params for an action from its registry definition.
 *
 * @param {string} actionId
 * @returns {Object} Default parameter values
 */
function buildDefaultParams(actionId) {
    const def = getAction(actionId);
    if (!def) return {};
    const defaults = {};
    for (const p of def.parameters || []) {
        if (p.defaultValue !== undefined) {
            defaults[p.name] = p.defaultValue;
        }
    }
    return defaults;
}

/**
 * Re-index params object after insert/remove so keys are sequential 0..N-1.
 *
 * @param {Object} params - { [stepIndex]: {...} }
 * @param {number} count - Expected number of steps
 * @returns {Object} Re-indexed params
 */
function reindexParams(params, count) {
    // Collect entries sorted by original key, then reassign 0..N-1
    const entries = Object.entries(params)
        .map(([k, v]) => [Number(k), v])
        .sort((a, b) => a[0] - b[0]);
    const result = {};
    for (let i = 0; i < Math.min(entries.length, count); i++) {
        result[i] = entries[i][1];
    }
    return result;
}

/**
 * Apply modifications from a modify_workflow intent to the current plan.
 *
 * @param {Object} currentPlan - The current ActionPlan { actions: PlannedAction[], summary }
 * @param {Object} currentParams - { [stepIndex]: { ...paramValues } }
 * @param {Array} modifications - [{ action: 'update_params'|'add_step'|'remove_step', ... }]
 * @returns {{ plan: Object, params: Object }} Updated plan and params.
 */
export function applyModifications(currentPlan, currentParams, modifications) {
    // Deep clone to avoid mutation
    const plan = JSON.parse(JSON.stringify(currentPlan));
    const params = JSON.parse(JSON.stringify(currentParams || {}));
    let structureChanged = false;

    for (const mod of modifications) {
        switch (mod.action) {
            case 'update_params': {
                // mod.step is 1-indexed; convert to 0-indexed
                const idx = mod.step - 1;
                if (idx >= 0 && idx < plan.actions.length) {
                    params[idx] = { ...(params[idx] || {}), ...mod.params };
                }
                break;
            }

            case 'add_step': {
                structureChanged = true;
                // mod.after is 1-indexed position to insert after (0 = insert at beginning)
                const insertIdx = mod.after || 0;
                const newAction = {
                    actionId: mod.actionId,
                    params: mod.params || {},
                    step: insertIdx + 1,
                };
                plan.actions.splice(insertIdx, 0, newAction);

                // Shift params for steps after the insertion point
                const shifted = {};
                for (const [k, v] of Object.entries(params)) {
                    const ki = Number(k);
                    if (ki >= insertIdx) {
                        shifted[ki + 1] = v;
                    } else {
                        shifted[ki] = v;
                    }
                }
                // Initialize params for the new step from registry defaults + provided params
                shifted[insertIdx] = { ...buildDefaultParams(mod.actionId), ...(mod.params || {}) };

                // Re-index and renumber steps
                Object.keys(shifted).forEach((k) => delete params[k]);
                Object.assign(params, reindexParams(shifted, plan.actions.length));
                for (let i = 0; i < plan.actions.length; i++) {
                    plan.actions[i].step = i + 1;
                }
                break;
            }

            case 'remove_step': {
                // mod.step is 1-indexed
                const removeIdx = mod.step - 1;
                if (removeIdx >= 0 && removeIdx < plan.actions.length) {
                    structureChanged = true;
                    plan.actions.splice(removeIdx, 1);

                    // Remove params entry and shift remaining down
                    delete params[removeIdx];
                    const shifted = {};
                    for (const [k, v] of Object.entries(params)) {
                        const ki = Number(k);
                        if (ki > removeIdx) {
                            shifted[ki - 1] = v;
                        } else {
                            shifted[ki] = v;
                        }
                    }
                    // Replace params
                    for (const k of Object.keys(params)) delete params[k];
                    Object.assign(params, reindexParams(shifted, plan.actions.length));

                    // Renumber steps
                    for (let i = 0; i < plan.actions.length; i++) {
                        plan.actions[i].step = i + 1;
                    }
                }
                break;
            }

            default:
                break;
        }
    }

    if (structureChanged && plan.executionMode === 'campaign'
        && plan.workflowId === 'hit-to-lead'
        && plan.source === 'campaign-verb-shortcut') {
        plan.workflowId = 'workflow-checker-campaign';
        plan.source = 'workflow-checker-edited';
        plan.summary = `Custom reviewed campaign: ${plan.actions
            .map((action) => action.actionId)
            .join(' → ')}.`;
    }

    return { plan, params };
}
